import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ShieldCheck, BadgeCheck, QrCode, User as UserIcon,
  ArrowLeft, Sparkles, Wallet, Star,
} from "lucide-react";

/** =====================================================================
 *  Digital iCard Mockup Atelier
 *  ---------------------------------------------------------------------
 *  Four distinct metallic finishes (Brushed Steel, Chrome Streak,
 *  Anodized Satin, Foil Holo) rendered across all four tier colors.
 *  Pulls the signed-in Trade Org's logo dynamically so committee staff
 *  can preview exactly what their members will receive.
 * ===================================================================== */

const TIER = {
  labor:     { label: "Labor",     color: "#4A4E51", glow: "#6B7280" },
  direction: { label: "Direction", color: "#D9383A", glow: "#F87171" },
  creative:  { label: "Creative",  color: "#6B4EFF", glow: "#A78BFA" },
  stage:     { label: "Stage",     color: "#EC4899", glow: "#F472B6" },
};

const SAMPLE = {
  name:      "Ananya Krishnan",
  role:      "Director of Photography",
  cardNo:    "CS-A12F38B0",
  memberNo:  "WICA/M/2024/00481",
  issuedAt:  "2026-02-14",
};

export default function ICardMockups() {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [tierKey, setTierKey] = useState("creative");
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    api.get("/onboard/me/organization").then(({ data }) => {
      if (data.organization) {
        setOrg(data.organization);
        if (data.organization.logo_path) {
          // Fetch logo as blob so the <img> tag can render auth-gated bytes.
          api.get(`/files/${data.organization.logo_path}`, { responseType: "blob" })
            .then((r) => setLogoUrl(URL.createObjectURL(r.data)))
            .catch(() => setLogoUrl(null));
        }
      }
    }).catch(() => {});
    return () => { if (logoUrl) URL.revokeObjectURL(logoUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tier = TIER[tierKey];
  const orgBrand = org?.brand_name || org?.legal_name || "TRADE ORG";

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="icard-mockups">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-white">
        <ArrowLeft size={14} /> Back to dashboard
      </Link>

      <div className="mt-4 text-[10px] tracking-[0.3em] uppercase text-white/55 flex items-center gap-2">
        <Sparkles size={11} className="text-[#F5B82E]" /> Atelier · Digital iCard Mockups
      </div>
      <h1 className="font-display text-4xl sm:text-6xl uppercase tracking-tight mt-2">
        Pick Your Finish
      </h1>
      <p className="mt-3 text-sm text-white/55 max-w-2xl">
        Four metallic treatments, each reading clearly across the four tier
        colors of the Call Sheet ecosystem. Your trade body&rsquo;s logo sits at
        the lead position; Call Sheet signs the bottom rail.
      </p>

      {/* TIER PICKER */}
      <div className="mt-8 flex flex-wrap gap-2" data-testid="tier-picker">
        {Object.entries(TIER).map(([key, t]) => (
          <button
            key={key}
            onClick={() => setTierKey(key)}
            data-testid={`tier-pick-${key}`}
            className={`px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.25em] border transition-all
              ${tierKey === key
                ? "border-white/40 bg-white/10 text-white"
                : "border-white/10 text-white/55 hover:border-white/25 hover:text-white"}`}
          >
            <span className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
                  style={{ background: t.color, boxShadow: `0 0 14px ${t.glow}` }} />
            {t.label}
          </button>
        ))}
        <div className="ml-auto text-[10px] tracking-[0.22em] uppercase text-white/35 self-center">
          Sample · {SAMPLE.name} · {orgBrand}
        </div>
      </div>

      {/* MOCKUP GRID */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-7" data-testid="mockup-grid">
        <Variant
          variant="brushed"
          title="01 · Brushed Steel"
          subtitle="Industrial · ID-card formal · readable at speed"
          tier={tier}
          orgBrand={orgBrand}
          logoUrl={logoUrl}
        />
        <Variant
          variant="chrome"
          title="02 · Chrome Streak"
          subtitle="Mirror polish · light bar · luxury energy"
          tier={tier}
          orgBrand={orgBrand}
          logoUrl={logoUrl}
        />
        <Variant
          variant="anodized"
          title="03 · Anodized Satin"
          subtitle="Color-saturated matte · deep tier hue"
          tier={tier}
          orgBrand={orgBrand}
          logoUrl={logoUrl}
        />
        <Variant
          variant="foil"
          title="04 · Foil Holo"
          subtitle="Iridescent shift · premium tier · collector feel"
          tier={tier}
          orgBrand={orgBrand}
          logoUrl={logoUrl}
        />
      </div>

      <div className="mt-10 cs-card rounded-2xl p-6 text-sm text-white/65 leading-relaxed">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/45 mb-2">Notes</div>
        Tier color is fixed nationally — same Direction red on every Direction-tier
        member regardless of body. Your trade org logo + colors layer on top at the
        lead position so members visually anchor to <span className="text-white">your</span> association first.
        Call Sheet is always the bottom rail signature.
      </div>
    </div>
  );
}

/* ============ ONE METALLIC VARIANT ============ */
function Variant({ variant, title, subtitle, tier, orgBrand, logoUrl }) {
  return (
    <div data-testid={`mockup-${variant}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-display text-lg uppercase tracking-tight">{title}</div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-white/45">{subtitle}</div>
      </div>
      <Card variant={variant} tier={tier} orgBrand={orgBrand} logoUrl={logoUrl} />
    </div>
  );
}

function Card({ variant, tier, orgBrand, logoUrl }) {
  const styles = useMemo(() => buildVariantStyle(variant, tier), [variant, tier]);
  return (
    <div
      className="cs-grain rounded-2xl p-6 sm:p-7 relative overflow-hidden"
      style={styles.shell}
      data-variant={variant}
    >
      {/* Decorative layer */}
      <div className="absolute inset-0 pointer-events-none" style={styles.deco} />
      {variant === "foil" && (
        <div className="absolute inset-0 pointer-events-none mix-blend-screen"
             style={{
               background: "conic-gradient(from 0deg at 50% 50%, #ff6ec7, #6c8cff, #4ade80, #fbbf24, #ff6ec7)",
               opacity: 0.18,
               filter: "blur(28px)",
             }} />
      )}
      {variant === "chrome" && (
        <div className="absolute -inset-1 pointer-events-none"
             style={{
               background: "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.22) 47%, rgba(255,255,255,0.05) 53%, transparent 70%)",
               mixBlendMode: "screen",
             }} />
      )}

      {/* ROW 1 — Trade Org lead */}
      <div className="relative flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden shrink-0"
          style={{
            background: logoUrl ? "white" : tier.color,
            boxShadow: `0 0 0 1.5px ${tier.color}66, 0 10px 26px -10px ${tier.color}aa`,
          }}
          data-testid="mockup-org-logo"
        >
          {logoUrl
            ? <img src={logoUrl} alt="org logo" className="w-full h-full object-contain" />
            : <span className="font-display font-bold text-[12px] text-white">
                {orgBrand.slice(0, 2).toUpperCase()}
              </span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base sm:text-lg uppercase tracking-tight truncate" style={{ color: styles.headingColor }}>
            {orgBrand}
          </div>
          <div className="text-[9px] tracking-[0.22em] uppercase text-white/55 truncate">
            Member iCard · {tier.label} Tier
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/40 border border-white/15">
          <ShieldCheck size={10} className="text-[#F5B82E]" />
          <span className="text-[8.5px] tracking-[0.28em] uppercase text-white/85">Verified</span>
        </div>
      </div>

      <div className="my-5 h-px" style={styles.divider} />

      {/* ROW 2 — Identity */}
      <div className="relative grid grid-cols-[auto_1fr] gap-4 items-center">
        <div className="relative">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl border flex items-center justify-center"
               style={{ background: styles.avatarBg, borderColor: styles.borderSoft }}>
            <UserIcon size={28} className="text-white/45" />
          </div>
          <div className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-full flex items-center justify-center shadow"
               style={{ background: tier.color }}>
            <BadgeCheck size={12} className="text-black" />
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[9px] tracking-[0.3em] uppercase text-white/45">Name</div>
          <div className="font-display text-lg sm:text-xl uppercase tracking-tight truncate">{SAMPLE.name}</div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Meta label="Role" value={SAMPLE.role.toUpperCase()} />
            <Meta label="Member #" value={SAMPLE.memberNo} mono />
          </div>
        </div>
      </div>

      <div className="my-5 h-px" style={styles.divider} />

      {/* ROW 3 — Tier + QR */}
      <div className="relative flex items-end justify-between gap-4">
        <div>
          <div className="text-[9px] tracking-[0.3em] uppercase text-white/45">Tier</div>
          <div className="inline-flex items-center gap-2 mt-1">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: tier.color, boxShadow: `0 0 14px ${tier.glow}` }} />
            <span className="font-display uppercase tracking-tight">{tier.label}</span>
          </div>
          <div className="mt-2 text-[10px] tracking-[0.22em] uppercase text-white/55">
            Card # {SAMPLE.cardNo}
          </div>
        </div>
        <div className="w-20 h-20 rounded-lg border flex items-center justify-center"
             style={{ background: styles.qrBg, borderColor: styles.borderSoft }}>
          <QrCode size={62} className="text-white/75" />
        </div>
      </div>

      {/* ROW 4 — Powered by */}
      <div className="relative mt-5 pt-4 border-t flex items-center justify-between"
           style={{ borderColor: styles.borderSoft }}>
        <div className="text-[9px] tracking-[0.3em] uppercase" style={{ color: styles.subtleText }}>
          Issued · {new Date(SAMPLE.issuedAt).toLocaleDateString()}
        </div>
        <div className="text-[9px] tracking-[0.32em] uppercase whitespace-nowrap" style={{ color: styles.wordmarkBase }}>
          Powered by{" "}
          <span className="font-display font-bold" style={{ color: styles.wordmarkCall }}>CALL</span>
          <span className="font-display font-bold" style={{ color: styles.wordmarkSheet }}>SHEET</span>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div className="min-w-0">
      <div className="text-[9px] tracking-[0.3em] uppercase text-white/40">{label}</div>
      <div className={`text-[11px] text-white/90 truncate ${mono ? "font-mono" : "font-medium"}`}>{value}</div>
    </div>
  );
}

/* ============ Per-variant style recipes ============ */
function buildVariantStyle(variant, tier) {
  const c = tier.color;
  const soft = "rgba(255,255,255,0.10)";
  const divider = "linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)";

  if (variant === "brushed") {
    return {
      shell: {
        background:
          "repeating-linear-gradient(90deg, #1a1c1f 0px, #1a1c1f 1px, #16181b 1px, #16181b 2px)," +
          "linear-gradient(180deg, #1e2024, #0f1114)",
        border: `1px solid rgba(255,255,255,0.10)`,
        boxShadow: `0 30px 60px -30px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.10), inset 0 0 0 1px ${c}22`,
      },
      deco: {
        background: `radial-gradient(circle at 85% 0%, ${c}33 0%, transparent 45%)`,
      },
      divider: { background: divider },
      headingColor: "#E6E8EC",
      avatarBg: "rgba(255,255,255,0.05)",
      qrBg: "rgba(0,0,0,0.5)",
      borderSoft: soft,
      subtleText: "rgba(255,255,255,0.45)",
      wordmarkBase: "rgba(255,255,255,0.65)",
      wordmarkCall: "#FFFFFF",
      wordmarkSheet: "#C0C0C0",
    };
  }
  if (variant === "chrome") {
    return {
      shell: {
        background:
          "linear-gradient(160deg, #d6d9de 0%, #8b8f96 30%, #2c2e33 55%, #8b8f96 80%, #d6d9de 100%)",
        border: `1px solid rgba(255,255,255,0.35)`,
        boxShadow: `0 30px 70px -28px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 0 0 2px ${c}55`,
      },
      deco: {
        background:
          `radial-gradient(circle at 20% 110%, ${c}55 0%, transparent 40%),` +
          `radial-gradient(circle at 90% -10%, ${c}33 0%, transparent 40%)`,
        mixBlendMode: "multiply",
      },
      divider: { background: "linear-gradient(90deg, transparent, rgba(0,0,0,0.35), transparent)" },
      headingColor: "#101216",
      avatarBg: "rgba(0,0,0,0.18)",
      qrBg: "rgba(255,255,255,0.55)",
      borderSoft: "rgba(0,0,0,0.25)",
      subtleText: "rgba(16,18,22,0.55)",
      wordmarkBase: "rgba(16,18,22,0.70)",
      wordmarkCall: "#0B0C0F",
      wordmarkSheet: "#3A3D43",
    };
  }
  if (variant === "anodized") {
    return {
      shell: {
        background:
          `linear-gradient(155deg, ${c} 0%, ${shade(c, -0.55)} 55%, ${shade(c, -0.75)} 100%)`,
        border: `1px solid ${shade(c, 0.25)}`,
        boxShadow: `0 30px 70px -25px ${c}99, inset 0 1px 0 ${shade(c, 0.35)}66, inset 0 -40px 80px ${shade(c, -0.6)}aa`,
      },
      deco: {
        background:
          `radial-gradient(circle at 30% 10%, ${shade(c, 0.4)}55 0%, transparent 40%),` +
          `radial-gradient(circle at 100% 100%, ${shade(c, -0.7)}cc 0%, transparent 55%)`,
      },
      divider: { background: `linear-gradient(90deg, transparent, ${shade(c, 0.3)}88, transparent)` },
      headingColor: "#FFFFFF",
      avatarBg: "rgba(0,0,0,0.20)",
      qrBg: "rgba(0,0,0,0.45)",
      borderSoft: "rgba(255,255,255,0.18)",
      subtleText: "rgba(255,255,255,0.55)",
      wordmarkBase: "rgba(255,255,255,0.75)",
      wordmarkCall: "#FFFFFF",
      wordmarkSheet: "#E5E7EB",
    };
  }
  // foil holo
  return {
    shell: {
      background:
        "linear-gradient(160deg, #0c0d10 0%, #0a0a0e 60%, #0e0e12 100%)",
      border: `1px solid rgba(255,255,255,0.14)`,
      boxShadow: `0 30px 80px -30px rgba(0,0,0,0.95), inset 0 1px 0 rgba(255,255,255,0.10), 0 0 0 1px ${c}33`,
    },
    deco: {
      background:
        "conic-gradient(from 0deg at 50% 50%, rgba(255,110,199,0.20), rgba(108,140,255,0.18), rgba(74,222,128,0.18), rgba(251,191,36,0.20), rgba(255,110,199,0.20))",
      filter: "blur(30px)",
      opacity: 0.55,
    },
    divider: { background: divider },
    headingColor: "#FFFFFF",
    avatarBg: "rgba(255,255,255,0.06)",
    qrBg: "rgba(0,0,0,0.5)",
    borderSoft: soft,
    subtleText: "rgba(255,255,255,0.45)",
    wordmarkBase: "rgba(255,255,255,0.65)",
    wordmarkCall: "#FFFFFF",
    wordmarkSheet: "#C0C0C0",
  };
}

/* mini color helper — lighten/darken hex by amount in [-1, 1] */
function shade(hex, amt) {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const f = (v) => {
    const out = amt >= 0
      ? Math.round(v + (255 - v) * amt)
      : Math.round(v * (1 + amt));
    return Math.max(0, Math.min(255, out)).toString(16).padStart(2, "0");
  };
  return `#${f(r)}${f(g)}${f(b)}`;
}
